import {
  CASE_SCHEMA_ID,
  CASE_SCHEMA_VERSION,
  emptyCanonicalData,
  type CanonicalCase,
  type CanonicalCaseData,
  type CaseDraft,
  type ProcedureSupervisionDetail,
  type RegionalTechniqueDetail,
  type ValueOrigin,
} from "./canonical-case";
import {
  defaultClinicalDate,
  defaultSession,
  normalizeAgeCategory,
  normalizeAsa,
  normalizeExplicitDayCase,
  normalizeSession,
  normalizeSpecialty,
  normalizeSupervision,
} from "./normalization";
import { nextIsoTimestamp } from "./timestamps";

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? { ...(value as UnknownRecord) }
    : {};
}

function text(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return fallback;
}

function rawImportCellText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * iOS exposes provenance as `[[String: String]]`, encoded into this string
 * field. Early Studio builds stored a single JSON object instead. Normalise
 * both representations to the shared array-of-string-maps contract while
 * retaining nested source values as JSON strings rather than discarding them.
 */
export function normalizeRawImportRowsData(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";

  let parsed: unknown = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value) as unknown;
    } catch {
      // Preserve opaque legacy provenance that is not JSON.
      return value;
    }
  }

  const candidates = Array.isArray(parsed) ? parsed : [parsed];
  const rows: Array<Record<string, string>> = [];
  for (const candidate of candidates) {
    if (candidate === null || typeof candidate !== "object" || Array.isArray(candidate)) {
      return typeof value === "string" ? value : "";
    }
    rows.push(Object.fromEntries(
      Object.entries(candidate as Record<string, unknown>)
        .map(([key, item]) => [key, rawImportCellText(item)]),
    ));
  }
  return JSON.stringify(rows);
}

function textArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => text(item).trim()).filter(Boolean);
  }
  const raw = text(value).trim();
  return raw ? [raw] : [];
}

function stringMap(value: unknown): Record<string, string> {
  return Object.fromEntries(
    Object.entries(record(value)).map(([key, item]) => [key, text(item)]),
  );
}

function procedureDetails(value: unknown): Record<string, ProcedureSupervisionDetail> {
  return Object.fromEntries(
    Object.entries(record(value)).map(([name, detailValue]) => {
      const detail = record(detailValue);
      return [name, {
        ...detail,
        supervision: text(detail.supervision),
        grade: text(detail.grade),
      }];
    }),
  );
}

function regionalDetails(value: unknown): Record<string, RegionalTechniqueDetail> {
  return Object.fromEntries(
    Object.entries(record(value)).map(([name, detailValue]) => {
      const detail = record(detailValue);
      return [name, {
        ...detail,
        technique: text(detail.technique, "US"),
        catheter: text(detail.catheter),
        supervision: text(detail.supervision),
        needleUsed: text(detail.needleUsed),
        localAnaesthetics: textArray(detail.localAnaesthetics),
        volume: text(detail.volume),
        ...(Object.hasOwn(detail, "notes") ? { notes: text(detail.notes) } : {}),
      }];
    }),
  );
}

function valueOrigins(value: unknown): Record<string, ValueOrigin | string> {
  return stringMap(value);
}

function stableFingerprint(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return `[${value.map(stableFingerprint).join("\u001f")}]`;
  if (typeof value === "object") {
    return `{${Object.entries(value as UnknownRecord)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${key}:${stableFingerprint(item)}`)
      .join("\u001f")}}`;
  }
  return String(value);
}

interface OriginFieldComparison {
  key: string;
  previous: unknown;
  current: unknown;
  populated: boolean;
}

function reconciledValueOrigins(
  existing: CanonicalCase,
  draft: CaseDraft,
  data: CanonicalCaseData,
): Record<string, ValueOrigin | string> {
  const origins: Record<string, ValueOrigin | string> = {
    ...existing.data.valueOrigins,
    ...draft.valueOrigins,
  };
  const fields: OriginFieldComparison[] = [
    { key: "date", previous: existing.date, current: draft.date, populated: Boolean(draft.date.trim()) },
    { key: "session", previous: existing.session, current: normalizeSession(draft.session, existing.session || "Morning"), populated: Boolean(draft.session.trim()) },
    { key: "hospital", previous: existing.hospital, current: draft.hospital, populated: Boolean(draft.hospital.trim()) },
    { key: "procedure", previous: existing.data.procedure, current: data.procedure, populated: Boolean(data.procedure) },
    { key: "age", previous: existing.data.age, current: data.age, populated: Boolean(data.age) },
    { key: "asa", previous: existing.data.asa, current: data.asa, populated: Boolean(data.asa) },
    { key: "spec", previous: existing.data.spec, current: data.spec, populated: Boolean(data.spec) },
    { key: "urg", previous: existing.data.urg, current: data.urg, populated: Boolean(data.urg) },
    { key: "dayCase", previous: existing.data.dayCase ?? "", current: data.dayCase ?? "", populated: Boolean(data.dayCase) },
    { key: "duration", previous: existing.data.duration, current: data.duration, populated: Boolean(data.duration) },
    { key: "role", previous: existing.data.role, current: data.role, populated: Boolean(data.role) },
    { key: "maint", previous: existing.data.maint, current: data.maint, populated: Boolean(data.maint) },
    { key: "equipment", previous: existing.data.anaesthetic_equipment, current: data.anaesthetic_equipment, populated: Boolean(data.anaesthetic_equipment) },
    { key: "breathing", previous: existing.data.breathing, current: data.breathing, populated: Boolean(data.breathing) },
    { key: "airway", previous: existing.data.airway, current: data.airway, populated: Boolean(data.airway) },
    { key: "awake", previous: existing.data.airway_awake_status, current: data.airway_awake_status, populated: Boolean(data.airway_awake_status) },
    {
      key: "regional",
      previous: { values: existing.data.regional, details: existing.data.regional_detail },
      current: { values: data.regional, details: data.regional_detail },
      populated: data.regional.length > 0,
    },
    { key: "access_situ", previous: existing.data.access_situ, current: data.access_situ, populated: data.access_situ.length > 0 },
    {
      key: "access_new",
      previous: { values: existing.data.access_new, details: existing.data.proc_supervision },
      current: { values: data.access_new, details: data.proc_supervision },
      populated: data.access_new.length > 0,
    },
    { key: "position", previous: existing.data.position, current: data.position, populated: Boolean(data.position) },
    { key: "postop", previous: existing.data.postop, current: data.postop, populated: Boolean(data.postop) },
    { key: "complications", previous: existing.data.complications, current: data.complications, populated: data.complications.length > 0 },
    { key: "sup", previous: existing.data.sup, current: data.sup, populated: Boolean(data.sup) },
    { key: "supGrade", previous: existing.data.supGrade, current: data.supGrade, populated: Boolean(data.supGrade) },
    { key: "teaching", previous: existing.data.teaching, current: data.teaching, populated: data.teaching.length > 0 },
    { key: "funding", previous: existing.data.funding, current: data.funding, populated: Boolean(data.funding) },
    { key: "insurer", previous: existing.data.insurer, current: data.insurer, populated: Boolean(data.insurer) },
    { key: "notes", previous: existing.data.notes, current: data.notes, populated: Boolean(data.notes) },
  ];

  for (const field of fields) {
    if (!field.populated) {
      delete origins[field.key];
    } else if (origins[field.key] === undefined
      || stableFingerprint(field.previous) !== stableFingerprint(field.current)) {
      origins[field.key] = "explicit";
    }
  }

  const customFieldIds = new Set([
    ...Object.keys(existing.data.customFields),
    ...Object.keys(data.customFields),
  ]);
  for (const identifier of customFieldIds) {
    const previous = existing.data.customFields[identifier]?.trim() ?? "";
    const current = data.customFields[identifier]?.trim() ?? "";
    if (!current) delete origins[identifier];
    else if (origins[identifier] === undefined || previous !== current) origins[identifier] = "explicit";
  }

  return origins;
}

function canonicalData(value: unknown): CanonicalCaseData {
  const raw = record(value);
  const data: CanonicalCaseData = {
    ...raw,
    entry_type: text(raw.entry_type, "anaestheticCase"),
    access_new: textArray(raw.access_new),
    access_situ: textArray(raw.access_situ),
    age: normalizeAgeCategory(raw.age),
    airway: text(raw.airway),
    airway_awake_status: text(
      raw.airway_awake_status ?? raw.awake ?? raw.airwayAwakeStatus,
    ),
    anaesthetic_equipment: text(
      raw.anaesthetic_equipment ?? raw.equipment ?? raw.anaestheticEquipment,
    ),
    asa: normalizeAsa(raw.asa),
    breathing: text(raw.breathing),
    complications: textArray(raw.complications),
    duration: text(raw.duration),
    maint: text(raw.maint ?? raw.anaestheticType),
    notes: text(raw.notes),
    position: text(raw.position),
    postop: text(raw.postop ?? raw.postOpDestination),
    procedure: text(raw.procedure ?? raw.procedureName),
    regional: textArray(raw.regional),
    role: text(raw.role),
    spec: normalizeSpecialty(raw.spec ?? raw.specialty),
    sup: normalizeSupervision(raw.sup ?? raw.supervisionLevel),
    supGrade: text(raw.supGrade ?? raw.supervisorGrade),
    teaching: textArray(raw.teaching),
    urg: text(raw.urg ?? raw.urgency),
    funding: text(raw.funding),
    insurer: text(raw.insurer),
    proc_supervision: procedureDetails(raw.proc_supervision),
    regional_detail: regionalDetails(raw.regional_detail),
    customFields: stringMap(raw.customFields),
    valueOrigins: valueOrigins(raw.valueOrigins),
  };

  const dayCase = normalizeExplicitDayCase(raw.dayCase);
  if (dayCase) data.dayCase = dayCase;
  else delete data.dayCase;

  for (let slot = 0; slot < 4; slot += 1) {
    const identifier = `custom-field-${slot}`;
    const legacy = text(raw[`custom_${slot}`]).trim();
    if (!data.customFields[identifier] && legacy) data.customFields[identifier] = legacy;
  }
  return data;
}

function timestampText(value: unknown, fallback: string): string {
  if (typeof value === "string" && value.trim()) return value;
  if (value instanceof Date) return value.toISOString();
  if (value && typeof value === "object" && "toDate" in value) {
    const toDate = (value as { toDate?: unknown }).toDate;
    if (typeof toDate === "function") {
      try {
        const parsed = (toDate as (this: unknown) => Date).call(value);
        if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) return parsed.toISOString();
      } catch {
        return fallback;
      }
    }
  }
  return fallback;
}

export function decodeCanonicalCase(
  value: unknown,
  documentId?: string,
  now = new Date(),
): CanonicalCase {
  const raw = record(value);
  const fallbackNow = now.toISOString();
  const id = text(raw.caseId).trim()
    || text(raw.id).trim()
    || text(documentId).trim()
    || crypto.randomUUID();
  const updatedAt = timestampText(raw.updatedAt ?? raw.lastUpdated, fallbackNow);
  const isDeleted = raw.isDeleted === true;
  const deletedAtRaw = raw.deletedAt;

  return {
    ...raw,
    id,
    caseId: id,
    date: isDeleted ? "" : text(raw.date),
    session: isDeleted ? "" : normalizeSession(raw.session, "Morning"),
    hospital: isDeleted ? "" : text(raw.hospital),
    data: isDeleted ? emptyCanonicalData() : canonicalData(raw.data),
    createdAt: timestampText(raw.createdAt, updatedAt),
    updatedAt,
    lastUpdated: timestampText(raw.lastUpdated, updatedAt),
    isDeleted,
    deletedAt: isDeleted
      ? timestampText(deletedAtRaw, updatedAt)
      : deletedAtRaw === null || deletedAtRaw === undefined || deletedAtRaw === ""
        ? null
        : timestampText(deletedAtRaw, updatedAt),
    deviceId: text(raw.deviceId),
    schemaId: CASE_SCHEMA_ID,
    schemaVersion: Math.max(Number(raw.schemaVersion) || 1, CASE_SCHEMA_VERSION),
    sourceSystem: isDeleted ? "" : text(raw.sourceSystem),
    sourceWorksheet: isDeleted ? "" : text(raw.sourceWorksheet),
    sourceCaseID: isDeleted ? "" : text(raw.sourceCaseID),
    rawPrimarySpecialty: isDeleted ? "" : text(raw.rawPrimarySpecialty),
    rawSecondarySpecialty: isDeleted ? "" : text(raw.rawSecondarySpecialty),
    normalizedPrimarySpecialty: isDeleted
      ? ""
      : normalizeSpecialty(raw.normalizedPrimarySpecialty ?? record(raw.data).spec),
    normalizedSecondarySpecialty: isDeleted
      ? ""
      : normalizeSpecialty(raw.normalizedSecondarySpecialty),
    rawImportRowsData: isDeleted ? "" : normalizeRawImportRowsData(raw.rawImportRowsData),
    entry_origin: isDeleted
      ? "native"
      : text(raw.entry_origin, raw.importBatchId ? "external_import" : "native"),
    importBatchId: isDeleted ? "" : text(raw.importBatchId),
    importOriginalFilename: isDeleted ? "" : text(raw.importOriginalFilename),
    importedAt: isDeleted || raw.importedAt === null || raw.importedAt === undefined
      ? null
      : timestampText(raw.importedAt, fallbackNow),
  };
}

export function encodeCanonicalCase(value: CanonicalCase): UnknownRecord {
  if (value.isDeleted) {
    const deletedAt = value.deletedAt ?? value.updatedAt;
    return {
      id: value.id,
      caseId: value.id,
      isDeleted: true,
      deletedAt,
      updatedAt: value.updatedAt,
      lastUpdated: value.lastUpdated,
      deviceId: value.deviceId,
      schemaId: CASE_SCHEMA_ID,
      schemaVersion: Math.max(value.schemaVersion, CASE_SCHEMA_VERSION),
    };
  }

  const data: UnknownRecord = {
    ...value.data,
    entry_type: value.data.entry_type,
    access_new: value.data.access_new,
    access_situ: value.data.access_situ,
    age: value.data.age,
    airway: value.data.airway,
    airway_awake_status: value.data.airway_awake_status,
    anaesthetic_equipment: value.data.anaesthetic_equipment,
    asa: value.data.asa,
    breathing: value.data.breathing,
    complications: value.data.complications,
    duration: value.data.duration,
    maint: value.data.maint,
    notes: value.data.notes,
    position: value.data.position,
    postop: value.data.postop,
    procedure: value.data.procedure,
    regional: value.data.regional,
    role: value.data.role,
    spec: value.data.spec,
    sup: value.data.sup,
    supGrade: value.data.supGrade,
    teaching: value.data.teaching,
    urg: value.data.urg,
    funding: value.data.funding,
    insurer: value.data.insurer,
    proc_supervision: value.data.proc_supervision,
    regional_detail: value.data.regional_detail,
    customFields: value.data.customFields,
    valueOrigins: value.data.valueOrigins,
  };
  delete data.equipment;
  delete data.anaestheticEquipment;
  delete data.awake;
  delete data.airwayAwakeStatus;
  delete data.procedureName;
  delete data.specialty;
  delete data.urgency;
  delete data.anaestheticType;
  delete data.supervisionLevel;
  delete data.supervisorGrade;
  delete data.postOpDestination;

  const explicitDayCase = normalizeExplicitDayCase(value.data.dayCase);
  if (explicitDayCase) data.dayCase = explicitDayCase;
  else delete data.dayCase;

  for (let slot = 0; slot < 4; slot += 1) {
    const customValue = value.data.customFields[`custom-field-${slot}`];
    if (customValue) data[`custom_${slot}`] = customValue;
    else delete data[`custom_${slot}`];
  }

  return {
    ...value,
    id: value.id,
    caseId: value.id,
    date: value.date,
    session: normalizeSession(value.session, "Morning"),
    hospital: value.hospital,
    data,
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
    lastUpdated: value.lastUpdated,
    isDeleted: false,
    deletedAt: null,
    deviceId: value.deviceId,
    schemaId: CASE_SCHEMA_ID,
    schemaVersion: Math.max(value.schemaVersion, CASE_SCHEMA_VERSION),
    rawImportRowsData: normalizeRawImportRowsData(value.rawImportRowsData),
    importedAt: value.importedAt,
  };
}

export function defaultCaseDraft(now = new Date(), primaryHospital = ""): CaseDraft {
  return {
    date: defaultClinicalDate(now),
    session: defaultSession(now),
    hospital: primaryHospital,
    age: "",
    asa: "2",
    procedure: "",
    spec: "General",
    urg: "Elective",
    duration: "",
    role: "Primary",
    maint: "GA-Volatile",
    anaesthetic_equipment: "",
    breathing: "Spontaneous",
    airway: "LMA",
    airway_awake_status: "Asleep",
    regional: [],
    access_situ: [],
    access_new: [],
    position: "Supine",
    dayCase: "",
    postop: "Ward",
    complications: [],
    sup: "4",
    supGrade: "",
    teaching: [],
    funding: "NHS",
    insurer: "Not applicable",
    notes: "",
    customFields: {},
    proc_supervision: {},
    regional_detail: {},
    valueOrigins: {},
  };
}

export function draftFromCase(value: CanonicalCase): CaseDraft {
  return {
    date: value.date,
    session: value.session,
    hospital: value.hospital,
    age: value.data.age,
    asa: value.data.asa,
    procedure: value.data.procedure,
    spec: value.data.spec,
    urg: value.data.urg,
    duration: value.data.duration,
    role: value.data.role,
    maint: value.data.maint,
    anaesthetic_equipment: value.data.anaesthetic_equipment,
    breathing: value.data.breathing,
    airway: value.data.airway,
    airway_awake_status: value.data.airway_awake_status,
    regional: [...value.data.regional],
    access_situ: [...value.data.access_situ],
    access_new: [...value.data.access_new],
    position: value.data.position,
    dayCase: value.data.dayCase ?? "",
    postop: value.data.postop,
    complications: [...value.data.complications],
    sup: value.data.sup,
    supGrade: value.data.supGrade,
    teaching: [...value.data.teaching],
    funding: value.data.funding,
    insurer: value.data.insurer,
    notes: value.data.notes,
    customFields: { ...value.data.customFields },
    proc_supervision: structuredClone(value.data.proc_supervision),
    regional_detail: structuredClone(value.data.regional_detail),
    valueOrigins: { ...value.data.valueOrigins },
  };
}

function draftData(draft: CaseDraft, existing?: CanonicalCaseData): CanonicalCaseData {
  const normalizedSupervision = normalizeSupervision(draft.sup);
  const procedureDetail = procedureDetails(draft.proc_supervision);
  const regionalDetail = regionalDetails(draft.regional_detail);
  const data: CanonicalCaseData = {
    ...(existing ?? emptyCanonicalData()),
    entry_type: existing?.entry_type ?? "anaestheticCase",
    access_new: [...draft.access_new],
    access_situ: [...draft.access_situ],
    age: normalizeAgeCategory(draft.age),
    airway: draft.airway.trim(),
    airway_awake_status: draft.airway_awake_status.trim(),
    anaesthetic_equipment: draft.anaesthetic_equipment.trim(),
    asa: normalizeAsa(draft.asa),
    breathing: draft.breathing.trim(),
    complications: [...draft.complications],
    duration: draft.duration.trim(),
    maint: draft.maint.trim(),
    notes: draft.notes.trim(),
    position: draft.position.trim(),
    postop: draft.postop.trim(),
    procedure: draft.procedure.trim(),
    regional: [...draft.regional],
    role: draft.role.trim(),
    spec: normalizeSpecialty(draft.spec),
    sup: normalizedSupervision,
    supGrade: normalizedSupervision === "4" ? "" : draft.supGrade.trim(),
    teaching: [...draft.teaching],
    urg: draft.urg.trim(),
    funding: draft.funding.trim(),
    insurer: draft.funding === "NHS" ? "Not applicable" : draft.insurer.trim(),
    proc_supervision: Object.fromEntries(
      Object.entries(procedureDetail).filter(([name]) => draft.access_new.includes(name)),
    ),
    regional_detail: Object.fromEntries(
      Object.entries(regionalDetail).filter(([name]) => draft.regional.includes(name)),
    ),
    customFields: { ...draft.customFields },
    valueOrigins: { ...(existing?.valueOrigins ?? {}), ...draft.valueOrigins },
  };
  const dayCase = normalizeExplicitDayCase(draft.dayCase);
  if (dayCase) data.dayCase = dayCase;
  else delete data.dayCase;
  return data;
}

export interface BuildCaseOptions {
  id?: string;
  now?: Date;
  deviceId: string;
  sourceSystem?: string;
  sourceWorksheet?: string;
  sourceCaseID?: string;
  rawImportRowsData?: string;
  entryOrigin?: string;
  importBatchId?: string;
  importOriginalFilename?: string;
  importedAt?: string | null;
}

export function buildCanonicalCase(draft: CaseDraft, options: BuildCaseOptions): CanonicalCase {
  const now = options.now ?? new Date();
  const timestamp = nextIsoTimestamp(now);
  const id = options.id ?? crypto.randomUUID();
  const speciality = normalizeSpecialty(draft.spec);
  return {
    id,
    caseId: id,
    date: draft.date,
    session: normalizeSession(draft.session, "Morning"),
    hospital: draft.hospital.trim(),
    data: draftData(draft),
    createdAt: timestamp,
    updatedAt: timestamp,
    lastUpdated: timestamp,
    isDeleted: false,
    deletedAt: null,
    deviceId: options.deviceId,
    schemaId: CASE_SCHEMA_ID,
    schemaVersion: CASE_SCHEMA_VERSION,
    sourceSystem: options.sourceSystem ?? "LogBookAnaes Studio",
    sourceWorksheet: options.sourceWorksheet ?? "",
    sourceCaseID: options.sourceCaseID ?? "",
    rawPrimarySpecialty: options.entryOrigin === "external_import" ? draft.spec.trim() : "",
    rawSecondarySpecialty: "",
    normalizedPrimarySpecialty: speciality,
    normalizedSecondarySpecialty: "",
    rawImportRowsData: normalizeRawImportRowsData(options.rawImportRowsData ?? ""),
    entry_origin: options.entryOrigin ?? "native",
    importBatchId: options.importBatchId ?? "",
    importOriginalFilename: options.importOriginalFilename ?? "",
    importedAt: options.importedAt ?? null,
  };
}

export function updateCanonicalCase(
  existing: CanonicalCase,
  draft: CaseDraft,
  now = new Date(),
  mutationId: string = crypto.randomUUID(),
): CanonicalCase {
  const timestamp = nextIsoTimestamp(now, existing.updatedAt, existing.lastUpdated);
  const data = draftData(draft, existing.data);
  data.valueOrigins = reconciledValueOrigins(existing, draft, data);
  return {
    ...existing,
    date: draft.date,
    session: normalizeSession(draft.session, existing.session || "Morning"),
    hospital: draft.hospital.trim(),
    data,
    normalizedPrimarySpecialty: normalizeSpecialty(draft.spec),
    updatedAt: timestamp,
    lastUpdated: timestamp,
    isDeleted: false,
    deletedAt: null,
    studio: { ...existing.studio, lastMutationId: mutationId },
  };
}

export function makeCaseTombstone(existing: CanonicalCase, now = new Date()): CanonicalCase {
  const timestamp = nextIsoTimestamp(now, existing.updatedAt, existing.lastUpdated, existing.deletedAt);
  return {
    ...existing,
    date: "",
    session: "",
    hospital: "",
    data: emptyCanonicalData(),
    sourceSystem: "",
    sourceWorksheet: "",
    sourceCaseID: "",
    rawPrimarySpecialty: "",
    rawSecondarySpecialty: "",
    normalizedPrimarySpecialty: "",
    normalizedSecondarySpecialty: "",
    rawImportRowsData: "",
    importBatchId: "",
    importOriginalFilename: "",
    importedAt: null,
    isDeleted: true,
    deletedAt: timestamp,
    updatedAt: timestamp,
    lastUpdated: timestamp,
  };
}
